"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import styles from "./index.module.css";

import SearchBar from "./searchbar";

const getImageUrl = (source, depth = 0) => {
  if (!source || depth > 2) return "";
  if (typeof source === "string") return source;

  const directImage =
    source.mediaThumb ||
    source.mediaUrl ||
    source.imageUrl ||
    source.coverPhoto ||
    source.coverImageUrl ||
    source.thumbnail ||
    source.url ||
    source.src;

  if (directImage) return directImage;

  if (Array.isArray(source.media) && source.media.length) {
    return getImageUrl(source.media[0], depth + 1);
  }

  if (source.image) {
    return getImageUrl(source.image, depth + 1);
  }

  return "";
};

const getHighResImage = (imageUrl) =>
  imageUrl?.replace("r200x200", "r1500x1500");

const CoverPage = ({
  organisation,
  activeCategory,
  searchTerm,
  activateSearch,
  handleSearchTerm,
  searchBarRef = null,
}) => {
  const [fontColor, setFontColor] = useState("black"); // default
  const [opacityClass, setOpacityClass] = useState("noOpacity");
  const [imageError, setImageError] = useState(false); // 👈 track if image fails
  const imageRef = useRef(null);
  const isCategoryHero = Boolean(activeCategory);
  const heroImage = useMemo(
    () =>
      isCategoryHero
        ? activeCategory?.imageUrl || ""
        : getImageUrl(organisation?.coverPhoto),
    [activeCategory?.imageUrl, isCategoryHero, organisation?.coverPhoto],
  );
  const heroTitle = isCategoryHero
    ? activeCategory?.name || organisation?.orgName || "Unnamed Organisation"
    : organisation?.orgName || "Unnamed Organisation";
  const heroDescription = isCategoryHero
    ? activeCategory?.description || organisation?.description || ""
    : organisation?.description || "No description available";

  const getLuminance = (r, g, b) => {
    const [red, green, blue] = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  };

  const determineFontColor = () => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = imageRef.current;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let r = 0,
        g = 0,
        b = 0,
        count = 0;
      for (let i = 0; i < data.length; i += 4 * 10) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }
      r = Math.round(r / count);
      g = Math.round(g / count);
      b = Math.round(b / count);

      const luminance = getLuminance(r, g, b);
      const isBright = luminance > 0.5;
      setFontColor(isBright ? "black" : "white");
      setOpacityClass(isBright ? "whiteOpacity" : "blackOpacity");
    } catch (err) {
      setFontColor("black");
      setOpacityClass("whiteOpacity");
    }
  };

  useEffect(() => {
    setImageError(false);
  }, [heroImage]);

  useEffect(() => {
    if (!heroImage) return;
    const img = imageRef.current;
    if (!img) return;

    const handleLoad = () => determineFontColor();
    const handleError = () => setImageError(true);

    if (img.complete) {
      if (img.naturalWidth === 0) {
        setImageError(true);
      } else {
        handleLoad();
      }
    } else {
      img.addEventListener("load", handleLoad);
      img.addEventListener("error", handleError);
    }

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [heroImage]);

  if (isCategoryHero && (!heroImage || imageError)) return null;

  const showFallback = imageError || !heroImage;

  return (
    <div  className={styles.heroPad} >
      <div
        className={styles.hero}
        style={
          !showFallback
            ? {
                "--custom-background": `url(${getHighResImage(heroImage)})`,
              }
            : {
                backgroundColor: "#f5f5f5", // 👈 fallback background
              }
        }
      >
        {!showFallback && (
          <img
            ref={imageRef}
            src={heroImage}
            alt={heroTitle || "Cover image"}
            style={{ display: "none" }}
            crossOrigin="anonymous"
          />
        )}

        <div className={`${opacityClass} ${styles.hideMobile}`}>
          <div className={styles.title} style={{ color: fontColor }}>
            {heroTitle}
          </div>
          {heroDescription && (
            <div className={styles.description} style={{ color: fontColor }}>
              {heroDescription}
            </div>
          )}
        </div>

        <SearchBar
          searchTerm={searchTerm}
          activateSearch={activateSearch}
          handleSearchTerm={handleSearchTerm}
          searchBarRef={searchBarRef}
        />
      </div>
    </div>
  );
};

export default CoverPage;
